// types.ts

export type Page =
  | "Dashboard"
  | "Orders"
  | "Products"
  | "Customers"
  | "Marketing"
  | "Unified Inbox"
  | "Channels"
  | "Reports"
  | "Shipments"
  | "Warehouse Inventory"
  | "Transactions"
  | "Invoices"
  | "Payment Gateways"
  | "Settings"
  | "Knowledge Hub"
  | "Connect Channel"
  | "Login"
  | "LoggedOut"
  | "SignUp"
  | "ForgotPassword"
  | "Billing"
  | "Team Management"
  | "Track"
  | "Admin Dashboard"
  | "Admin Clients"
  | "Admin Users"
  | "Admin Billing"
  | "Admin Inventory"
  | "Admin Orders"
  | "Admin Marketing"
  | "Admin Channels"
  | "Admin Settings"
  | "Admin Support"
  | "Bulk Sync"
  | "Admin Logs"
  | "Admin Returns"
  | "Add Client"
  | "Add User"
  | "Create Campaign"
  | "SignUp Welcome"
  | "Edit Client"
  | "Modal Demo"
  | "Onboarding"
  | "Pricing"
  | "DesignSystem"
  | "Health"
  | "AuthCallback"
  | "Support"
  | "Forbidden"
  | "NotAcceptable"
  | "ServerError"
  | "Privacy"
  | "Terms"
  | "FinalizeWorkspace"
  | "Operations Center"
  | "Inventory Reconciliation"
  | "DLQ Recovery";

import type { UserRole } from "./src/shared/roles.ts";
export {
  USER_ROLES,
  USER_ROLE_VALUES,
  normalizeUserRole,
  isUserRole,
  ROLE_LABELS,
} from "./src/shared/roles.ts";
export type { UserRole };

export type SubscriptionState =
  "trialing" | "active" | "past_due" | "suspended" | "canceled";

export type SystemAccessState = "full" | "limited" | "locked";

export type PermissionAction =
  | "view_billing"
  | "manage_billing"
  | "manage_users"
  | "manage_plan"
  | "view_all_clients"
  | "manage_all_plans"
  | "override_system_limits"
  | "view_audit_logs";

export interface AuthenticatedUser {
  id: string;
  nano_id?: string;
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl: string;
  title: string;
  // Updated role to restricted union
  role?: UserRole; // Optional because legacy data might not have it yet
  // State flags for Routing Guards
  is_signed_up: boolean;
  is_email_verified: boolean;
  is_logged_in: boolean;
  onboarding_status: "not_started" | "in_progress" | "completed";
  activation_status: "inactive" | "active";
  subscription_status?:
    "active" | "pending" | "past_due" | "canceled" | "trialing" | "inactive";
  client_nano_id?: string;
  client_short_id?: string;
  client_slug?: string;
  client_id?: string;
}

export interface Endpoint {
  method: "GET" | "POST" | "PUT" | "DELETE";
  path: string;
  description: string;
}

export interface APIModule {
  name: string;
  endpoints: Endpoint[];
}

export enum OrderStatus {
  Pending = "Pending",
  Processing = "Processing",
  Shipped = "Shipped",
  Delivered = "Delivered",
  Cancelled = "Cancelled",
}

export interface OrderItem {
  product_id: string; // UUID from products table
  quantity: number;
  // Extended fields often stored in JSONB or derived from lookup
  name?: string;
  unit_price?: number;
  id?: string; // Sometimes the item record itself has a UUID
}

export interface Order {
  id: string;
  customer: string; // Legacy: Customer Name
  customerId?: string; // UUID from customers table
  customerName?: string; // Explicit name
  customerEmail?: string; // Explicit email
  date: string;
  total: number;
  status: OrderStatus;
  channel?: string;
  items: OrderItem[];
  shippingAddress?: Address;
  slug?: string;
  short_id?: string;
  nano_id?: string;
  // New fields
  marketplace?: string | null;
  marketplace_order_id?: string | null;
  marketplace_metadata?: Record<string, any> | null;
  tags?: string[] | null;
  customer_phone?: string | null;
}

export interface LineItem {
  id: string;
  product_id?: string;
  product_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  sku?: string;
  is_custom?: boolean;
}

export interface Address {
  name: string;
  street1: string;
  street2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  phone?: string;
  email?: string;
}

export interface Product {
  id: string; // UUID in DB
  name: string;
  description: string;
  category: string;
  price: number;
  sku?: string;
  stock: number; // Deprecated: Use stock_quantity
  stock_quantity: number;
  low_stock_threshold: number;
  status: "Active" | "Archived" | "Low Stock"; // UI status
  imageUrl: string; // Deprecated: Use image_url
  image_url: string;
  sales?: number;
  slug?: string;
  short_id?: string;
  nano_id?: string;
}

export interface Channel {
  name: string;
  logoUrl: string;
  isConnected: boolean;
  sales: number;
  orders: number;
}

export enum TransactionStatus {
  Settled = "Settled",
  Pending = "Pending",
  Failed = "Failed",
  Refunded = "Refunded",
}

export interface Transaction {
  id: string;
  shortId?: string;
  nanoId?: string;
  orderId: string;
  customerName: string;
  dateTime: string;
  amount: number;
  paymentMethod: string;
  channel: string;
  status: TransactionStatus;
}

export enum CustomerSegment {
  HighSpender = "High Spender",
  Loyal = "Loyal",
  New = "New",
  AtRisk = "At-Risk",
}

export interface Customer {
  id: string; // UUID in DB
  shortId?: string;
  nanoId?: string;
  name: string;
  email: string;
  avatarUrl: string;
  joinedDate: string;
  totalOrders: number;
  totalSpent: number;
  segment: CustomerSegment;
  // Added for compatibility with Supabase & UI
  status?: "Active" | "Inactive";
  lastOrderDate?: string;
}

export enum CampaignStatus {
  Active = "Active",
  Paused = "Paused",
  Completed = "Completed",
  Draft = "Draft",
}

export interface MarketingCampaign {
  id: string; // UUID in DB
  name: string;
  channel: string;
  status: CampaignStatus;
  startDate: string;
  endDate: string;
  spend: number;
  revenue: number;
  conversions: number;
  roi?: number; // Calculated
}

export interface DashboardKpi {
  title: string;
  metricKey: "revenue" | "orders" | "customers" | "inventory" | "roas";
  value: string;
  mtdValue: string;
  change: string;
  changeType: "increase" | "decrease";
}

export interface SalesDataPoint {
  name: string;
  revenue: number;
  orders: number;
}

export interface DashboardData {
  kpis: DashboardKpi[];
  revenueSalesTrends: {
    chartData: SalesDataPoint[];
    aiAnnotation: string;
  };
  ordersOverview: {
    completed: number;
    pending: number;
    cancelled: number;
  };
  channelPerformance: { name: string; sales: number }[];
  customerInsights: {
    new: number;
    returning: number;
  };
  inventoryHealth: {
    inStock: number;
    lowStock: number;
    outOfStock: number;
    predictiveAlerts: number;
  };
  marketingRoi: {
    totalRevenue: number;
    totalSpend: number;
    underperformingAds: number;
  };
  aiInsights: { id: number; text: string }[];
  tasks: { id: number; text: string; page: Page }[];
}

export interface UserProfile {
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl: string;
  title: string;
  phone: string;
  timezone: string;
  language: string;
  client_slug?: string;
  client_short_id?: string;
  client_nano_id?: string;
}

export interface PasswordSettings {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface ChatMessage {
  role: "user" | "model";
  parts: { text: string }[];
}

export type SearchResultModule =
  | "Orders"
  | "Products"
  | "Customers"
  | "Marketing"
  | "Invoices"
  | "Help"
  | "Action"
  | "Transactions";

export interface OmniSearchResultItem {
  id: string | number;
  module: SearchResultModule;
  title: string;
  description?: string;
  lowStock?: boolean;
  isDelayed?: boolean;
  data?: any;
}

export type OmniSearchResults = Partial<
  Record<SearchResultModule, OmniSearchResultItem[]>
>;

export enum ShipmentStatus {
  Processing = "Processing",
  InTransit = "In-Transit",
  Delivered = "Delivered",
  Delayed = "Delayed",
  Cancelled = "Cancelled",
}

export interface Shipment {
  id: string;
  clientId: string;
  orderId: string;
  carrier: string;
  serviceLevel: string;
  trackingNumber?: string;
  labelUrl?: string;
  cost?: number;
  status: ShipmentStatus;
  shippedAt?: string;
  estimatedDelivery?: string;
}

export interface PackageDetails {
  weight: number;
  length: number;
  width: number;
  height: number;
}

export interface ShippingRate {
  id: string;
  carrier: string;
  service: string;
  rate: number;
  estDelivery: string;
}

export interface WarehouseStat {
  name: string;
  value: string;
}

export enum InventoryStatus {
  InStock = "In Stock",
  LowStock = "Low Stock",
  OutOfStock = "Out of Stock",
}

export interface InventoryItem {
  id: number;
  productName: string;
  productImageUrl: string;
  sku: string;
  warehouse: string;
  batchNumber?: string;
  lotNumber?: string;
  expiryDate?: string;
  quantity: number;
  status: InventoryStatus;
}

export enum InvoiceStatus {
  Paid = "Paid",
  Sent = "Sent",
  Overdue = "Overdue",
  Draft = "Draft",
}

export interface InvoiceItem {
  productId: number;
  productName: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

export interface Invoice {
  id: string;
  shortId?: string;
  nanoId?: string;
  orderId: string;
  customerName: string;
  dateIssued: string;
  dueDate: string;
  amount: number;
  status: InvoiceStatus;
  channel: string;
  items: InvoiceItem[];
}

export interface InvoiceKpi {
  title: string;
  value: string;
  change: string;
  changeType: "increase" | "decrease";
}

export type PaymentGatewayStatus =
  "Connected" | "Not Connected" | "Needs Attention";

export interface PaymentGateway {
  id: string;
  name: string;
  logoUrl: string;
  description: string;
  status: PaymentGatewayStatus;
}

export interface CODSettings {
  isEnabled: boolean;
  instructions: string;
}

export interface ApiKey {
  id: string;
  name: string;
  maskedKey: string;
  createdDate: string;
  lastUsed: string;
  key?: string; // Only available on creation
}

export interface OauthApp {
  id: string;
  name: string;
  logoUrl: string;
  permissions: string[];
}

export interface SyncPreferences {
  webhookUrl: string;
  syncFrequency: "realtime" | "15min" | "hourly" | "daily";
  dataToSync: {
    orders: boolean;
    products: boolean;
    customers: boolean;
    inventory: boolean;
  };
}

export interface ActiveSession {
  id: string;
  device: string;
  location: string;
  ipAddress: string;
  lastActive: string;
  isCurrentSession: boolean;
}

export type ChannelCategory =
  | "E-commerce"
  | "Marketplace"
  | "Social Commerce"
  | "Advertising"
  | "Shipping"
  | "Payments";

export interface AvailableChannel {
  id: number;
  name: string;
  logoUrl: string;
  description: string;
  category: ChannelCategory;
  isConnected: boolean;
  authType: "oauth" | "apikey";
  syncOptions: (
    "products" | "inventory" | "orders" | "customers" | "ads" | "payments"
  )[];
  region: "Global" | "North America" | "Europe" | "APAC" | "LatAm" | "MEA";
  supportedCountries: string[];
  paymentMethods?: string[]; // e.g. ['Card', 'COD', 'BNPL', 'Bank Transfer']
}

export enum RMAStatus {
  Pending = "Pending",
  Approved = "Approved",
  Processing = "Processing",
  Completed = "Completed",
  Rejected = "Rejected",
}

export interface ReturnRequest {
  id: string;
  orderId: string;
  customerName: string;
  email?: string;
  date: string;
  status: RMAStatus;
  reason?: string;
  condition?: string;
  comments?: string;
  refundAmount?: number;
  items?: OrderItem[];
}

export interface CarrierAccount {
  id: string;
  name: string;
  logoUrl: string;
  accountNumber: string;
  status: "Active" | "Inactive";
}

export interface ShippingSettings {
  defaultPackage: {
    weight: number;
    length: number;
    width: number;
    height: number;
  };
  addressValidation: "strict" | "suggest" | "none";
  labelFormat: "PDF" | "ZPL";
}

export enum InboxChannel {
  Email = "Email",
  WhatsApp = "WhatsApp",
  Instagram = "Instagram",
  Facebook = "Facebook",
  Twitter = "Twitter",
  SMS = "SMS",
  Shopify = "Shopify",
}

export enum AITag {
  Support = "Support",
  Sales = "Sales",
  Refund = "Refund",
  Complaint = "Complaint",
  General = "General Inquiry",
}

export enum Sentiment {
  Positive = "Positive",
  Neutral = "Neutral",
  Negative = "Negative",
}

export interface Message {
  id: string; // UUID
  conversation_id?: string;
  author: "customer" | "agent" | "system";
  text: string;
  timestamp: string; // ISO or formatted
  created_at?: string;
  is_read?: boolean;
  translatedText?: string;
  delivery_status?:
    "queued" | "sending" | "sent" | "delivered" | "failed" | "retrying";
  delivery_error?: string;
}

export interface Conversation {
  id: string; // UUID
  client_id?: string;
  customer_id?: string; // Standardize on snake_case
  customerId?: string; // Compat
  customerName: string;
  customerAvatar?: string;
  subject?: string;
  lastMessage: string;
  timestamp: string;
  isRead: boolean;
  channel: InboxChannel;
  status:
    | "open"
    | "awaiting_customer"
    | "awaiting_internal"
    | "awaiting_platform"
    | "automated"
    | "resolved"
    | "escalated"
    | "spam"
    | "closed"
    | "snoozed";
  aiTag: AITag;
  sentiment: Sentiment;
  messages: Message[];
  assigned_to?: string;
  sla_status?: "healthy" | "warning" | "breached";
  sla_expires_at?: string;
}

export interface SmartReply {
  id: number;
  text: string;
  tone: "Polite" | "Casual" | "Formal";
}

export interface KnowledgeBaseArticle {
  id: number;
  title: string;
  snippet: string;
}

export interface ActivityFeedItem {
  id: number;
  type: "order" | "email" | "support" | "view";
  description: string;
  timestamp: string;
}

export interface SyncOptions {
  products: "auto" | "manual";
  inventory: "realtime" | "scheduled";
  orders: "all" | "new";
  customers: boolean;
  ads: "realtime" | "daily";
  payments?: "realtime" | "daily" | "manual"; // Added for payment gateways/marketplaces
}

export interface ProductPerformanceReport {
  topSellers: Product[];
  slowMovers: Product[];
}
export interface ChannelPerformanceReport {
  chartData: { name: string; sales: number }[];
  tableData: {
    channel: string;
    revenue: number;
    orders: number;
    avgOrderValue: number;
  }[];
}
export interface CustomerInsightsReport {
  kpis: { title: string; value: string }[];
  segments: { name: CustomerSegment; count: number }[];
}
export interface MarketingAnalyticsReport {
  campaigns: MarketingCampaign[];
}
export interface AllReportsData {
  sales: { [key: string]: SalesDataPoint[] };
  productPerformance: ProductPerformanceReport;
  channelPerformance: ChannelPerformanceReport;
  customerInsights: CustomerInsightsReport;
  marketingAnalytics: MarketingAnalyticsReport;
}

export interface TransactionsDashboardData {
  kpis: InvoiceKpi[];
  transactions: Transaction[];
  revenueRefundTrend: {
    chartData: { name: string; revenue: number; refunds: number }[];
    aiAnnotation: string;
  };
  channelRevenue: { name: string; value: number }[];
  paymentMethodSplit: { name: string; value: number }[];
  settlementTimeline: {
    channel: string;
    amount: number;
    expectedDate: string;
    status: "On-time" | "Delayed";
  }[];
  aiInsights: string[];
}

export interface NotificationPreferences {
  globalEnable: boolean;
  channels: {
    inApp: boolean;
    email: boolean;
    sms: boolean;
    push: boolean;
  };
  categories: {
    [key: string]: { [key: string]: boolean };
    account: {
      newDeviceLogin: boolean;
      passwordChange: boolean;
      billingUpdates: boolean;
    };
    orders: {
      newOrder: boolean;
      orderShipped: boolean;
      orderCancelled: boolean;
    };
    products: {
      lowStock: boolean;
      outOfStock: boolean;
      bulkUploadStatus: boolean;
    };
    payments: {
      settlementReceived: boolean;
      refundProcessed: boolean;
      transactionFailed: boolean;
    };
  };
  frequency: "realtime" | "daily" | "weekly";
  quietHours: {
    enabled: boolean;
    startTime: string;
    endTime: string;
  };
}

export interface VideoTutorial {
  id: number;
  title: string;
  description: string;
  thumbnailUrl: string;
  duration: string;
  category: "Getting Started" | "Features" | "Integrations";
  script?: string;
}

export interface FAQItem {
  id: number;
  question: string;
  answer: string;
  category: string;
  views?: number;
  lastUpdated?: string;
}

export interface SupportTicket {
  id: string;
  client_id: string;
  user_id: string;
  subject: string;
  description: string;
  status: "Open" | "In Progress" | "Resolved" | "Closed";
  priority: "Low" | "Medium" | "High" | "Critical";
  assignee?: string;
  issue_type: "Technical" | "Billing" | "Feature" | "Security" | "Other";
  last_reply_at: string;
  created_at: string;
  updated_at: string;
  // For UI compatibility if needed
  lastUpdated?: string;
}

export type PaymentGatewayCategory =
  "Card Payments" | "Wallets" | "UPI" | "BNPL";

export interface AvailablePaymentGateway {
  id: string;
  name: string;
  logoUrl: string;
  description: string;
  category: PaymentGatewayCategory;
  isConnected: boolean;
  authType: "oauth" | "apikey";
  syncOptions: ("transactions" | "refunds" | "settlements" | "multiCurrency")[];
}

export interface PaymentGatewaySyncOptions {
  transactions: "realtime" | "scheduled";
  refunds: "auto" | "manual";
  settlements: "daily" | "weekly" | "monthly";
  multiCurrency: boolean;
}

export enum NotificationCategory {
  Payment = "payment",
  Order = "order",
  Error = "error",
  System = "system",
}

export interface Notification {
  id: string;
  category: NotificationCategory;
  title: string;
  timestamp: string; // ISO 8601 string
  isRead: boolean;
  link?: {
    page: Page;
    initialState?: any;
  };
}

export type PlanType = "Starter" | "Pro" | "Growth" | "Enterprise";

export interface BillingContact {
  name: string;
  email: string;
  companyName: string;
  phone: string;
  taxId?: string; // Sensitive field
  billingAddress?: string;
  country?: string;
}

export interface SubscriptionData {
  plan: PlanType;
  price: string;
  interval: "Monthly" | "Yearly";
  renewalDate: string;
  status: "Active" | "Cancelled" | "Trial" | "Past Due" | "Suspended";
  usage: {
    aiCredits: { used: number; total: number };
    storage: { usedGB: number; totalGB: number };
    users: { used: number; total: number };
  };
}

export interface OnboardingState {
  progress: number;
  steps: Record<string, boolean>;
  fullName?: string;
  isLive?: boolean;
  currentStep?: OnboardingStep;
}

export type OnboardingStep =
  | "company_setup"
  | "localization_setup"
  | "connect_channels"
  | "add_products"
  | "billing"
  | "invite_team"
  | "subscription_active";

export interface BillingOverrideLog {
  id: string;
  adminId: string;
  clientId: string;
  action:
    | "Plan Change"
    | "Status Change"
    | "Credit Applied"
    | "Refund Issued"
    | "Usage Override"
    | "Manual Invoice";
  reason: string;
  timestamp: string;
  details?: string;
}

export interface PlanDetails {
  id: PlanType;
  name: string;
  price: number;
  interval: "Monthly" | "Yearly";
  features: string[];
  limits: {
    users: number;
    storageGB: number;
    aiCredits: number;
  };
  isActive: boolean;
}

export interface SuperAdminDashboardData {
  mrr: number;
  arr: number;
  subscribers: {
    active: number;
    trial: number;
    pastDue: number;
    cancelled: number;
  };
  revenueByPlan: { plan: string; value: number }[];
}

export interface ClientSubscriptionDetails extends SubscriptionData {
  clientId: string;
  clientName: string;
  trialEndsAt?: string;
  lastInvoiceDate?: string;
  lastInvoiceAmount?: number;
  nextRenewalDate: string;
}

declare global {
  interface Window {
    Razorpay: any;
  }
}
